import type { CityConfig } from "../city-template/types";

export const shorelineConfig: CityConfig = {
  city: "Shoreline",
  state: "Washington",
  stateAbbr: "WA",
  slug: "shoreline",
  route: "/cities/shoreline",
  title: "Emergency Passport Shoreline WA | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Shoreline, Washington — as fast as 24 hours. Fast passport renewal and new passport applications for Shoreline, Lake Forest Park, Kenmore, and all North King County residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Shoreline, WA",
  heroSubtitle: "Shoreline travelers — from Richmond Beach waterfront residents to light rail corridor commuters to North Shore families — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving all of Shoreline, North Seattle, and the North King County communities.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Scenic%20aerial%20view%20of%20Shoreline%20Washington%20in%20North%20King%20County%20bordering%20Seattle%20with%20Richmond%20Beach%20Saltwater%20Park%20and%20Puget%20Sound%20shoreline%2C%20residential%20neighborhoods%20with%20Pacific%20Northwest%20evergreen%20landscape%2C%20modern%20suburban%20atmosphere%20with%20light%20rail%20connectivity%2C%20Olympic%20Mountains%20visible%20across%20the%20water%2C%20warm%20golden%20afternoon%20sunlight%20over%20the%20Sound%2C%20North%20Shore%20community%2C%20professional%20city%20photography%20without%20text%20or%20people&width=1800&height=700&seq=shoreline-hero-001&orientation=landscape",
  heroImageAlt: "Shoreline Washington North King County with Richmond Beach and Puget Sound",
  geoPosition: "47.7556;-122.3417",
  geoPlacename: "Shoreline, Washington",
  lastModified: "2026-06-05",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "Approximately 10 miles south of Shoreline via I-5",
  },
  nearbyCitiesZips: [
    { name: "Shoreline", zips: "98133, 98155, 98160, 98177" },
    { name: "Lake Forest Park", zips: "98155" },
    { name: "Kenmore", zips: "98028" },
    { name: "Bothell", zips: "98011, 98012, 98021, 98041" },
    { name: "Lynnwood", zips: "98036, 98037, 98046, 98087" },
    { name: "Mountlake Terrace", zips: "98043" },
    { name: "Edmonds", zips: "98020, 98026" },
    { name: "Woodway", zips: "98020" },
    { name: "Briar", zips: "98036" },
    { name: "Esperance", zips: "98026" },
    { name: "North City", zips: "98155" },
    { name: "Richmond Highlands", zips: "98133" },
    { name: "Innis Arden", zips: "98177" },
    { name: "The Highlands", zips: "98177" },
    { name: "Seattle (North Seattle)", zips: "98103, 98105, 98115, 98125, 98133" },
    { name: "Kirkland", zips: "98033, 98034" },
    { name: "Mill Creek", zips: "98012, 98082" },
    { name: "Mukilteo", zips: "98275" },
    { name: "Everett", zips: "98201, 98203, 98204" },
    { name: "Woodinville", zips: "98072, 98077" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to Seattle Passport Agency — just 10 minutes south via I-5" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-anchor-line", title: "North Seattle Gateway", desc: "Bordering Seattle — serving Shoreline, Lake Forest Park, Kenmore, and North King County" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any North King County location" },
  ],
  internalLinks: [
    { name: "Expedited passport in Seattle, WA", route: "/cities/seattle" },
    { name: "Emergency passport help in Bellevue, WA", route: "/cities/bellevue" },
    { name: "Urgent passport services in Kirkland, WA", route: "/cities/kirkland" },
    { name: "Washington passport services overview", route: "/states/washington" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Shoreline, Washington remotely via secure registered courier. Our registered couriers hand-deliver your documents to the Seattle Passport Agency (300 5th Ave, Suite 600, Seattle, WA 98104) — approximately 10 minutes south via I-5. Shoreline residents can ship from any UPS or FedEx in North King County.",
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeDistance: "Serving Shoreline, Lake Forest Park, Kenmore, Bothell, Mountlake Terrace, Edmonds, and all of North King County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Shoreline and North King County. From Richmond Beach Saltwater Park on the Puget Sound to the Aurora Avenue corridor, from the North City neighborhood to the residential communities along the Seattle border — we serve all of Shoreline and the North Shore region.",
  nearbyCitiesIntro: "We serve Shoreline and all nearby communities including Lake Forest Park, Kenmore, Bothell, Lynnwood, Mountlake Terrace, Edmonds, and Woodway. We also serve North Seattle, Kirkland, and south Snohomish County. Our service covers all of North King County.",
  howItWorksIntro: "Most Shoreline travelers complete everything online and ship documents overnight. No need to drive 10 minutes south on I-5 to Seattle — our couriers handle the agency delivery and always pick the fastest route.",
  mistakesIntro: "Shoreline is a peaceful North King County suburb bordering Seattle with waterfront access, light rail connectivity, and a strong community feel. Here are the most common passport mistakes we see from North Shore travelers.",
  testimonialsIntro: "Shoreline travelers — from Richmond Beach residents to North City families to North King County professionals — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Shoreline, WA? Here are the most common ones we hear from North King County travelers.",
  statePageRoute: "/states/washington",
  statePageName: "Washington",
  dmvWebsite: "https://www.dol.wa.gov",
  dmvLabel: "Washington DOL — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive to downtown Seattle", "Parking and traffic in downtown Seattle"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Seattle Passport Agency — just 10 minutes south", "No downtown parking or I-5 traffic hassle", "Perfect for North Shore and North King County travelers", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Seattle Passport Agency — just 10 minutes south", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Cannot upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Shoreline travelers with immediate departure needs — as fast as 24 hours. Our couriers use the Seattle Passport Agency. Perfect for North King County families, waterfront residents, and light rail commuters.",
      bullets: ["24–48 hour turnaround available", "Seattle Passport Agency — just 10 minutes south courier", "North Shore and North King County travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Shoreline adults with a passport issued within the last 15 years. Skip the post office appointment entirely.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your North King County address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Shoreline residents and families — including North Shore families, waterfront residents, and North King County communities.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Shoreline residents who lost their passport before international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Shoreline and North King County families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Shoreline and North King County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Shoreline WA", description: "Urgent passport processing for Shoreline Washington travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Shoreline WA", description: "Fast passport renewal for Shoreline Washington residents with a previously-issued passport." },
    { name: "First-Time Passport Shoreline WA", description: "New passport application services for Shoreline Washington residents applying for their first passport." },
    { name: "Child Passport Shoreline WA", description: "Minor passport services for Shoreline Washington families with children under 16." },
  ],
  testimonials: [
    {
      name: "Rebecca A.",
      location: "Shoreline, WA",
      rating: 5,
      text: "Trip to London with 7 days notice and my passport was expired. Fast Passport Center had my renewed passport in 2 days. I shipped from the UPS Store on Aurora and everything was perfect. Incredibly convenient for North Shore residents.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Peter & Nina K.",
      location: "Lake Forest Park, WA",
      rating: 5,
      text: "Family trip to Costa Rica with 10 days notice. Three passports — one adult renewal and two first-time for our kids. Done in 4 days. We shipped from FedEx in Lynnwood and the whole process was seamless.",
      service: "Family Passport Bundle",
    },
    {
      name: "Thomas H.",
      location: "Richmond Beach, WA",
      rating: 5,
      text: "Business trip to Singapore with 8 days notice. Fast Passport Center walked me through every step and had my passport in 3 days. Living right on the water, it's great having Seattle just minutes away with this courier service.",
      service: "Emergency Passport",
    },
    {
      name: "Maria G.",
      location: "Kenmore, WA",
      rating: 5,
      text: "Lost my passport three weeks before a family reunion in Italy. Fast Passport Center replaced it in 4 days. The close Seattle proximity means incredibly fast courier turnaround for North King County.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "sho-1",
      name: "Shoreline Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "15225 Aurora Ave N",
      city: "Shoreline",
      state: "WA",
      zip: "98133",
      phone: "(206) 362-3102",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Shoreline, near Aurora Avenue corridor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Shoreline acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient to central Shoreline, North City, and Aurora corridor.",
      icon: "ri-mail-line",
    },
    {
      id: "sho-2",
      name: "Richmond Beach Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2356 NW 195th Pl",
      city: "Shoreline",
      state: "WA",
      zip: "98177",
      phone: "(206) 546-1351",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Richmond Beach, approximately 3 miles west of central Shoreline",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Richmond Beach, Innis Arden, and The Highlands residents. Smaller facility — book well in advance during summer.",
      icon: "ri-mail-line",
    },
    {
      id: "sho-3",
      name: "Lynnwood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6817 208th St SW",
      city: "Lynnwood",
      state: "WA",
      zip: "98036",
      phone: "(425) 771-1313",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Lynnwood, approximately 5 miles north of Shoreline via I-5",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Lynnwood and south Snohomish County. Ample parking near Alderwood Mall. Good alternative for north Shoreline residents.",
      icon: "ri-mail-line",
    },
    {
      id: "sho-4",
      name: "Edmonds Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 5th Ave S",
      city: "Edmonds",
      state: "WA",
      zip: "98020",
      phone: "(425) 771-0244",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Edmonds, approximately 5 miles northwest of Shoreline",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Edmonds, Woodway, and west Shoreline residents. Near the Edmonds waterfront and ferry terminal.",
      icon: "ri-mail-line",
    },
    {
      id: "sho-5",
      name: "King County Clerk — Seattle",
      type: "County Clerk Passport Acceptance",
      address: "516 3rd Ave, Room W-105",
      city: "Seattle",
      state: "WA",
      zip: "98104",
      phone: "(206) 477-3080",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Downtown Seattle Courthouse — approximately 10 minutes south via I-5",
      website: "https://www.kingcounty.com/depts/courts/clerk.aspx",
      websiteLabel: "King County Clerk",
      notes: "County Clerk passport acceptance. Extended hours. Shoreline residents benefit from the closest proximity to downtown Seattle of any North King County city.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — I-5 traffic to downtown Seattle", fpc: "No — ship from Shoreline" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "North Shore Advantage", agency: "No special guidance", fpc: "Yes — Shoreline and North King County expertise" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Shoreline families and North King County residents often discover passport issues just before summer waterfront vacations. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo services on Aurora Avenue or at North City sometimes produce photos that meet ID requirements but fail passport standards. Photos must be exactly 2x2 inches on a white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at King County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking Acceptance Facilities Early Enough",
      description: "Shoreline Post Office appointments fill during summer and holiday seasons. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-road-map-line",
      title: "Assuming Shoreline is Far from Seattle",
      description: "Shoreline borders Seattle directly — the passport agency is only 10 minutes away. Yet many residents still try the self-service postal route because they don't realize how close they are. Our courier service takes full advantage of this incredible proximity for the fastest turnaround in North King County.",
    },
    {
      icon: "ri-time-line",
      title: "Assuming 4–6 Weeks Is Enough",
      description: "Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service. Richmond Beach summer season and North King County family vacations peak May–August — plan ahead.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in the Shoreline area or North King County." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx in Shoreline, Lynnwood, or Edmonds." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier submits to the Seattle Passport Agency — 10 minutes south, the fastest time from any Washington suburb." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Shoreline address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Shoreline, WA?", a: "Fast Passport Center offers emergency passport processing in Shoreline, Washington in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Seattle Passport Agency (300 5th Ave, Suite 600, Seattle, WA 98104) — just 10 minutes south via I-5. Shoreline has the fastest courier time of any Washington suburb." },
    { q: "Does Shoreline have its own passport agency?", a: "No — but Shoreline borders Seattle directly. The Seattle Passport Agency is approximately 10 minutes south via I-5, making Shoreline one of the closest suburbs to the regional passport agency in the entire state. Fast Passport Center leverages this proximity for the fastest turnaround in North King County." },
    { q: "Can I get an expedited passport in Lake Forest Park, Kenmore, or Bothell?", a: "Absolutely. Fast Passport Center serves all of North King County including Lake Forest Park, Kenmore, Bothell, and all surrounding communities. Our service works the same way everywhere in the North Shore region." },
    { q: "What airport do Shoreline travelers use for international flights?", a: "Seattle-Tacoma International Airport (SEA) is the primary airport — approximately 25 minutes from Shoreline via I-5. SEA offers nonstop flights to Europe, Asia, Mexico, Canada, and domestic destinations. Always verify passport validity for at least 6 months past your return date." },
    { q: "Is Shoreline the closest suburb to the Seattle Passport Agency?", a: "Shoreline is one of the closest — approximately 10 minutes via I-5, comparable to other close-in North Seattle suburbs. Fast Passport Center's courier service takes full advantage of this proximity to provide the fastest turnaround times in North King County." },
    { q: "How much does an expedited passport cost in Shoreline, WA?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Shoreline?", a: "Walgreens, CVS, and Rite Aid stores throughout Shoreline offer passport photos. UPS Store and FedEx Office locations also provide them. Locations on Aurora Avenue, in North City, and at Richmond Beach offer walk-in service. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Does Fast Passport Center serve the North Seattle light rail corridor?", a: "Yes. Fast Passport Center serves all of North King County along the light rail corridor including Shoreline, Northgate, Roosevelt, and the University District. Light rail access makes Shoreline one of the most connected suburbs to downtown Seattle and the passport agency." },
  ],
  photoLocations: [
    { name: "Walgreens — Aurora Avenue", address: "15236 Aurora Ave N, Shoreline, WA 98133", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Rite Aid — North City", address: "17202 15th Ave NE, Shoreline, WA 98155", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Shoreline", address: "15501 Westminster Way N, Shoreline, WA 98133", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Lynnwood", address: "19620 Hwy 99, Lynnwood, WA 98036", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "WA DOL — Shoreline", address: "16041 Aurora Ave N, Shoreline, WA 98133", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Washington Department of Licensing for driver's licenses, REAL ID, and vehicle registration. Online appointments strongly recommended.", featured: true },
    { name: "WA DOL — Lynnwood", address: "18023 Hwy 99, Lynnwood, WA 98037", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Serves Lynnwood and north Shoreline. Full REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "25 minutes south of Shoreline via I-5",
      address: "17801 International Blvd, Seattle, WA 98158",
      note: "Primary airport for Shoreline and North King County. Nonstop flights to Europe, Asia, Mexico, Canada, and domestic destinations. Alaska Airlines and Delta Air Lines hub. Shoreline's location near I-5 provides direct access to the airport.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Flight Info",
      featured: true,
    },
    {
      code: "PAE",
      name: "Paine Field (Snohomish County)",
      distance: "20 minutes north of Shoreline via I-5",
      address: "3220 100th St SW, Everett, WA 98204",
      note: "Alternative airport for north Shoreline residents. Alaska Airlines and United serve select domestic destinations. Free parking and short security lines.",
      flightUrl: "https://www.painefield.com/",
      flightLabel: "PAE Flight Info",
      featured: false,
    },
  ],
};